import { signatureHtml, type Account, type UserSignature, t } from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { mySignature, whoOf } from '../lib/signature';
import { rich } from '../lib/rich';

/**
 * Semnătura din mailurile trimise în numele omului. Două feluri: cartea de
 * vizită făcută de Procumi (funcția, telefonul, logo-ul firmei) sau semnătura
 * pe care o are deja în Outlook, lipită aici. Previzualizarea e cum o vede
 * furnizorul, într-un cadru izolat (fără scripturi).
 */
export function SignatureEditor({
  account,
  toast,
  onSaved,
}: {
  account: Account;
  toast: Toast;
  onSaved: (s: UserSignature) => void;
}) {
  const saved = mySignature(account);
  const [mode, setMode] = useState(saved.mode);
  const [title, setTitle] = useState(saved.title);
  const [phone, setPhone] = useState(saved.phone);
  const [logo, setLogo] = useState(saved.logo);
  // ce s-a lipit acum; până atunci, semnătura din Outlook salvată
  const [pasted, setPasted] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const who = whoOf(account);
  const hasLogo = !!account.company.logoAt;

  // imaginile de pe calculator (cid:, file:) nu ajung la furnizor: serverul le scoate la salvare
  const localImg = /<img\b(?![^>]*\bsrc\s*=\s*["']?https:)[^>]*>/gi;
  const lipite = pasted ?? saved.html;
  const lost = (lipite.match(localImg) ?? []).length;
  const draft: UserSignature = { mode, title, phone, logo, html: lipite.replace(localImg, '') };
  const outlookEmpty = mode === 'outlook' && !draft.html.trim();
  const preview = outlookEmpty ? '' : signatureHtml(draft, who);
  const dirty =
    mode !== saved.mode ||
    title !== saved.title ||
    phone !== saved.phone ||
    logo !== saved.logo ||
    pasted != null;

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    setPasted(html || esc(text).replace(/\r?\n/g, '<br>'));
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.saveSignature({
        mode,
        title,
        phone,
        logo,
        ...(mode === 'outlook' ? { html: pasted ?? saved.html } : {}),
      });
      onSaved(r.signature);
      setPasted(null);
      toast(
        r.dropped
          ? t(
              'Semnătura e salvată. Am scos {0} doar pe calculatorul tău: furnizorul n-ar fi văzut-o.',
              r.dropped === 1 ? t('o imagine care era') : t('{0} imagini care erau', r.dropped),
            )
          : t('Semnătura e salvată'),
      );
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sig">
      <div className="sig-h">
        <b>{t('Semnătura din mailuri')}</b>
        <span className="muted">
          {t('La finalul cererilor, follow-up-urilor și comenzilor trimise în numele tău.')}
        </span>
      </div>

      <div className="sig-modes" role="radiogroup" aria-label={t('Felul semnăturii')}>
        {(
          [
            ['card', t('Făcută de Procumi'), t('Carte de vizită, cu logo-ul firmei')],
            ['outlook', t('Din Outlook'), t('Cea pe care o folosești deja')],
          ] as const
        ).map(([k, label, hint]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={mode === k}
            className={mode === k ? 'on' : undefined}
            onClick={() => setMode(k)}
          >
            <b>{label}</b>
            <span>{hint}</span>
          </button>
        ))}
      </div>

      {mode === 'card' ? (
        <div className="sig-fields">
          <label className="f" htmlFor="sig-title">
            {t('Funcția')}
            <input
              id="sig-title"
              className="inp"
              value={title}
              maxLength={80}
              placeholder={t('ex. Achiziții')}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="f" htmlFor="sig-phone">
            {t('Telefon')}
            <input
              id="sig-phone"
              className="inp"
              value={phone}
              maxLength={40}
              inputMode="tel"
              placeholder={t('ex. 0722 000 000')}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="check-line sig-logo">
            <input
              type="checkbox"
              checked={logo && hasLogo}
              disabled={!hasLogo}
              onChange={(e) => setLogo(e.target.checked)}
            />
            <span>
              {t('Cu logo-ul firmei')}
              {hasLogo ? null : (
                <span className="muted">
                  {' '}
                  {t('· firma n-are încă logo; îl pune administratorul în Contul firmei')}
                </span>
              )}
            </span>
          </label>
        </div>
      ) : (
        <div className="sig-fields">
          <ol className="sig-steps">
            <li>
              {rich(
                'În Outlook: <b>Fișier → Opțiuni → Mail → Semnături</b> (pe web: Setări → Conturi → Semnături).',
              )}
            </li>
            <li>{t('Selectezi semnătura și o copiezi: Ctrl+A, apoi Ctrl+C.')}</li>
            <li>{t('O lipești în chenarul de mai jos: Ctrl+V.')}</li>
          </ol>
          <div
            className={`sig-paste${pasted ? ' got' : ''}`}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-label={t('Lipește aici semnătura din Outlook')}
            onPaste={onPaste}
            onKeyDown={(e) => {
              // doar lipire: textul scris de mână nu are rost aici
              if (!(e.ctrlKey || e.metaKey) && e.key.length === 1) e.preventDefault();
            }}
          >
            {pasted ? t('Lipită. Vezi mai jos cum arată.') : t('Lipește aici (Ctrl+V)')}
          </div>
          {lost ? (
            <p className="hint warn-text sig-lost">
              {t(
                '{0} doar pe calculatorul tău, deci furnizorul n-ar vedea-o. Pentru logo, folosește varianta „Făcută de Procumi”.',
                lost === 1 ? t('O imagine e') : t('{0} imagini sunt', lost),
              )}
            </p>
          ) : null}
        </div>
      )}

      <div className="sig-prev">
        <span className="muted">{t('Așa o vede furnizorul')}</span>
        {preview ? (
          <iframe
            title={t('Semnătura, cum o vede furnizorul')}
            sandbox=""
            srcDoc={t(
              '<!doctype html><meta charset="utf-8"><body style="margin:12px 14px;font:400 15px/22px Archivo,\'Segoe UI\',Arial,sans-serif;color:#0F1D33">Cu stimă,{0}</body>',
              preview,
            )}
          />
        ) : (
          <div className="sig-empty muted">{t('Lipește semnătura ca s-o vezi aici.')}</div>
        )}
      </div>

      <div className="sig-acts">
        <button className="btn primary" disabled={busy || !dirty || outlookEmpty} onClick={() => void save()}>
          {busy ? t('Se salvează…') : t('Salvează semnătura')}
        </button>
      </div>
    </section>
  );
}
